import type {
  UnifiedProduct,
  UnifiedProductCustomAttribute,
  UnifiedProductTypedAttributeValue,
} from '../types/UnifiedProduct';

export const buildCustomAttribute = (
  key: string,
  value: UnifiedProductTypedAttributeValue,
): UnifiedProductCustomAttribute => ({ key, value, attributeType: null });

export const unifiedProduct: UnifiedProduct = {
  allowsGiftMessage: true,
  allowsGiftReceipt: true,
  attributes: {
    allowsGiftWrap: true,
    highlyRated: false,
    starBrand: true,
  },
  available: true,
  backInStockAt: null,
  breadcrumbCategoryId: 4471,
  code: 890605,
  customAttributes: [
    buildCustomAttribute('dominant_colour', { value: 'Silver' }),
    buildCustomAttribute('material', {
      values: ['Crystal (Swarovski)', 'Gold Plated (14Ct)', 'Pearl', 'Silver Plated'],
    }),
    buildCustomAttribute('pim_made_in_britain', { value: 'Made In Britain' }),
    buildCustomAttribute('low_commitment_product_fl', { boolValue: false }),
    buildCustomAttribute('compliance_category', {
      value: 'None - No Compliance Category Applies',
    }),
    buildCustomAttribute('delivery_cost', { numValue: 0 }),
    buildCustomAttribute('product_features', { values: ['Made to Order', 'Personalised'] }),
    buildCustomAttribute('pim_product_type', {
      values: ['jewellery', 'jewellery > body jewellery', 'jewellery > body jewellery > anklets'],
    }),
    buildCustomAttribute('product_type_level_1', { value: 'jewellery' }),
    buildCustomAttribute('product_type_level_2', { value: 'body jewellery' }),
    buildCustomAttribute('product_type_level_3', { value: 'anklets' }),
    buildCustomAttribute('returnable', { value: 'Returnable' }),
  ],
  delivery: {
    deliveryByEvent: null,
    freeDomesticDelivery: true,
  },
  description: 'A personalised silver anklet, hand finished in our Norfolk studio.',
  dimensions: 'Length: 25cm',
  freeGiftWrap: false,
  images: [
    {
      altText: 'Personalised silver anklet',
      gravity: 'center',
      height: 1000,
      href: 'https://cdn.notonthehighstreet.com/fs/75/fc/{size}_silver-anklet.jpg',
      position: 1,
      title: 'Personalised Silver Anklet',
      uri: 'https://cdn.notonthehighstreet.com/fs/75/fc/{size}_silver-anklet.jpg',
      width: 1000,
    },
  ],
  ingredients: null,
  inventoryStatus: 'made_to_order',
  isNew: false,
  materials: ['Silver Plated', 'Pearl'],
  metaDescription: 'Personalised silver anklet, handmade in Norfolk.',
  metaRobotsIndexable: true,
  navigation: [
    {
      options: [
        { href: 'https://notonthehighstreet.com/jewellery', rel: '/jewellery', title: 'Jewellery' },
      ],
      title: 'You may also like',
      type: 'relatedCategories',
    },
  ],
  onSale: false,
  otherInformation: 'Comes in a gift box.',
  partnerInfo: {
    id: 12345,
    links: [
      {
        href: 'https://notonthehighstreet.com/lisaangeljewellery',
        rel: '/lisaangeljewellery',
        title: 'Lisa Angel',
        type: null,
      },
    ],
    location: 'Norwich, Norfolk',
    logo: 'https://cdn.notonthehighstreet.com/fs/aa/bb/logo.jpg',
    metaTagDescription: null,
    name: 'Lisa Angel',
    shortcode: 'lisaangeljewellery',
  },
  preSalePrices: null,
  prices: [
    { amount: 2450, currency: 'GBP', exponent: 2 },
    { amount: 4700, currency: 'AUD', exponent: 2 },
  ],
  productLinks: [
    {
      href: 'https://notonthehighstreet.com/lisaangeljewellery/product/personalised-silver-anklet',
      rel: '/lisaangeljewellery/product/personalised-silver-anklet',
      title: null,
      type: null,
    },
  ],
  purchasable: true,
  promotions: [
    { id: '1297', endDate: '2026-09-29T23:59:59Z' },
    { id: '1298', endDate: '2026-10-06T23:59:59Z' },
  ],
  ratings: {
    averageRating: 4.5625,
    ratingCount: 16,
  },
  rawAvailability: 'available',
  salePercentage: 0,
  shippingInfo: 'Dispatched within 3 working days.',
  specialFeatures: {
    ecoFriendly: false,
    exclusive: false,
    madeInBritain: true,
    personalisable: true,
    requiresPhotoUpload: false,
  },
  title: 'Personalised Silver Anklet',
  variationsAvailable: 'Also available in gold',
};

/**
 * Shallow merge, matching buildUnifiedProductSummary. Override a nested object wholesale rather
 * than expecting a deep merge.
 */
export const buildUnifiedProduct = (
  overrides?: Partial<UnifiedProduct>,
): Readonly<UnifiedProduct> => ({
  ...unifiedProduct,
  ...overrides,
});
